<template>
  <div ref="searchRef" class="search">
    <InputWrapper ref="containerRef" @click="handleClickWrapper">
      <input
        ref="inputRef"
        v-model="searchTerm"
        type="text"
        placeholder="Look for..."
        @focus="handleFocus"
        @focusout="handleFocusOut"
      />
      <template #append>
        <v-icon v-if="searchTerm" small data-test="vsautocomplete-clear-option" @click.stop="clear">
          close
        </v-icon>
        <v-icon v-else> search</v-icon>
      </template>
    </InputWrapper>
    <div v-if="expanded" ref="popoverRef" class="popover" :style="floatingStyles">
      <div>
        <chip-filter
          class="ma-2 pt-2 pb-2"
          :items="categories"
          :value="selectedCategory"
          @update="selectCategory"
        />
      </div>
      <picker-list-virtualized ref="virtualizedList" :items="items" @near-bottom="fetchNextPage">
        <template #item="{ item }">
          <v-list-item dense two-line @click.stop="clickRow(item)">
            <v-list-item-content>
              <vs-text truncate :title="item.title">
                {{ item.title }}
              </vs-text>
              <vs-text v-if="item.subTitle" truncate type="subdued" :title="item.subTitle">
                {{ item.subTitle }}
              </vs-text>
            </v-list-item-content>
          </v-list-item>
        </template>
        <template #after>
          <v-container v-if="isFetching" class="my-2">
            <v-row justify="center">
              <vs-text center>loading...</vs-text>
            </v-row>
          </v-container>
        </template>
      </picker-list-virtualized>
      <div v-if="showSkeleton">
        <v-skeleton-loader type="list-item-two-line" width="50%"></v-skeleton-loader>
        <v-skeleton-loader type="list-item-two-line" width="50%"></v-skeleton-loader>
        <v-skeleton-loader type="list-item-two-line" width="50%"></v-skeleton-loader>
      </div>
      <div v-if="noResultsMessage" class="no-data">
        <v-container class="mb-3">
          <v-row justify="center">
            <vs-text center>{{ noResultsMessage }}</vs-text>
          </v-row>
        </v-container>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import InputWrapper from '@/components/vision/Inputs/InputWrapper.vue'
import { computed, ref } from 'vue'
import { onClickOutside } from '@vueuse/core'
import { useFloating, autoUpdate, flip, offset, size } from '@floating-ui/vue'
import VsText from '@/components/vision/VsText.vue'
import PickerListVirtualized from '@/components/vision/next/picker-list/PickerListVirtualized.vue'
import { SearchOptions, useFVSearchQuery } from '@/api/field-view'
import ChipFilter from '@/components/vision/next/ChipFilter.vue'
import { SearchResultDTO } from '@/models/fv-search-result-dto'
import { useRouter } from 'vue-router/composables'

const router = useRouter()

const categories = [
  { id: 'locations', label: 'Locations' },
  { id: 'resources', label: 'Resources' },
  { id: 'jobs', label: 'Jobs' },
]
const selectedCategory = ref(categories[0])

const searchTerm = ref<string>()

const inputRef = ref<HTMLInputElement | null>(null)
const popoverRef = ref<HTMLElement | null>(null)
const containerRef = ref<HTMLElement | null>(null)
const searchRef = ref<HTMLElement | null>(null)

const expanded = ref<boolean>(false)

const { floatingStyles } = useFloating(containerRef, popoverRef, {
  strategy: 'fixed',
  open: expanded,
  middleware: [
    flip(),
    offset(4),
    size({
      apply({ rects, elements }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
        })
      },
    }),
  ],
  whileElementsMounted(...args) {
    return autoUpdate(...args, { animationFrame: true })
  },
})

onClickOutside(searchRef, () => {
  expanded.value = false
})

const handleClickWrapper = () => {
  if (!expanded.value) {
    inputRef.value?.focus()
  }
}

const handleFocus = () => {
  expanded.value = true
}

const selectCategory = (item) => {
  selectedCategory.value = item
}

const handleFocusOut = (event: FocusEvent) => {
  // event.relatedTarget is the component that is gaining focus.
  // In Safari, onClick event on button won't focus the button, so event.relatedTarget = null
  // In other browsers, onClick event on button will focus it, so event.relatedTarget = button itself
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/button#clicking_and_focus
  if (event.relatedTarget && !searchRef.value?.contains(event.relatedTarget as Node)) {
    expanded.value = false
  }
}

const searchOptions = computed<SearchOptions>(() => {
  return {
    searchTerm: searchTerm.value,
    category: selectedCategory.value.id,
  } as any
})

const clear = () => {
  searchTerm.value = ''
}

const clickRow = ({ id, title, source }) => {
  expanded.value = false
  if (source === 'jobs') {
    searchTerm.value = title.split(' ')[0]?.replace('#', '')
    router.push({
      name: 'fvnext-ticket-info-panel-direct',
      params: { id },
    })
    return
  }
  searchTerm.value = title
  router.push({
    name: 'fvnext-info-panel',
    params: { id, source },
  })
}

const {
  data: searchResults,
  isFetched,
  isFetching,
  isInitialLoading,
  fetchNextPage,
} = useFVSearchQuery(searchOptions)

const items = computed(() => {
  return Object.freeze(
    searchResults.value?.pages
      .flatMap((p) => p.data)
      .map((s) => ({
        id: s.id,
        title: s.title,
        source: s.type,
        subTitle: buildSubTitleForRow(s),
      })) || [],
  )
})
const showSkeleton = computed(() => {
  if (items.value.length === 0) {
    if (isInitialLoading.value || isFetching.value) {
      return true
    }
  }
})

const noResultsMessage = computed(() => {
  if (items.value.length === 0) {
    if (!(isInitialLoading.value || !isFetched.value)) {
      return 'No results found for "' + searchOptions.value.searchTerm + '"'
    }
  }
  return ''
})

const buildSubTitleForRow = (item: SearchResultDTO) => {
  if (item.type === 'locations') {
    return [item.details.locationType, item.details.parentName, item.details.ownerName]
      .filter(Boolean)
      .join(' · ')
  } else if (item.type === 'resources') {
    return [item.details.resourceType, item.details.status, item.details.ownerName]
      .filter(Boolean)
      .join(' · ')
  } else if (item.type === 'jobs') {
    return [item.details.divisionName, item.details.hubName, item.details.destinationLocation]
      .filter(Boolean)
      .join(' · ')
  }

  return ''
}
</script>
<style scoped>
.search {
  width: 100%;
  max-width: 350px;
}

.popover {
  z-index: var(--elevation-popover);
  background-color: var(--color-white);
  border: 1px solid var(--color-grey--lighter);
  border-radius: var(--space-base);
  box-sizing: border-box;
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  width: 100%;
  height: auto;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  box-shadow: 0px 2px 3px 1px #272c3a80;
}

.search:deep(.field) {
  box-shadow: 0px 2px 3px 1px #272c3a80;
}
.search:deep(.field:focus-within),
.search:deep(.field:hover) {
  border: 1px solid var(--color-grey--lighter);
}
</style>
